import { useState, useEffect, useCallback, useRef } from 'react';
import api, { getErrorMessage } from '../services/api';
import Layout from '../components/Layout';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ' at ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const dueUrgency = (iso) => {
  if (!iso) return 'normal';
  const diff = new Date(iso) - new Date();
  if (diff < 0) return 'overdue';
  if (diff < 24 * 60 * 60 * 1000) return 'urgent';
  if (diff < 3 * 24 * 60 * 60 * 1000) return 'soon';
  return 'normal';
};

// ── Toast ────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    error: 'bg-red-50 border-red-200 text-red-800',
  };
  const icons = {
    success: 'M5 13l4 4L19 7',
    error: 'M6 18L18 6M6 6l12 12',
  };

  return (
    <div className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-lg animate-slide-down ${styles[type]}`}>
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[type]} />
      </svg>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ── Create Assignment Modal ─────────────────────────────────────────────────

const CreateAssignmentModal = ({ batches, classes, onSave, onCancel, loading }) => {
  const [form, setForm] = useState({
    batch_id: '',
    class_id: '',
    title: '',
    description: '',
    due_date: '',
    attachment_url: '',
  });
  const [formError, setFormError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormError('');
  };

  // Filter classes by selected batch
  const filteredClasses = form.batch_id
    ? classes.filter((c) => c.batch_id === form.batch_id)
    : classes;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.batch_id) return setFormError('Please select a batch.');
    if (!form.title.trim()) return setFormError('Assignment title is required.');
    if (!form.due_date) return setFormError('Due date is required.');

    onSave({
      batch_id: String(form.batch_id),
      class_id: form.class_id ? String(form.class_id) : null,
      title: form.title.trim(),
      description: form.description.trim() || null,
      due_date: new Date(form.due_date).toISOString(),
      attachment_url: form.attachment_url.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="text-lg font-bold text-gray-900">Create Assignment</h3>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{formError}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Batch</label>
            <select
              name="batch_id"
              value={form.batch_id}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white"
              required
            >
              <option value="">Select batch</option>
              {batches.map((b) => (
                <option key={b.id} value={b.id}>{b.batch_name} — {b.course_title || 'Course'}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Class (optional)</label>
            <select
              name="class_id"
              value={form.class_id}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white"
            >
              <option value="">No specific class</option>
              {filteredClasses.map((c) => (
                <option key={c.id} value={c.id}>{c.title} — {formatDate(c.scheduled_at)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
            <input
              ref={inputRef}
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              placeholder="e.g. Build a REST API"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm resize-none"
              placeholder="Instructions for students..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Due Date</label>
            <input
              type="datetime-local"
              name="due_date"
              value={form.due_date}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Reference Link (optional)</label>
            <input
              type="text"
              name="attachment_url"
              value={form.attachment_url}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              placeholder="Google Form, Drive, GitHub link..."
            />
            <p className="text-xs text-gray-400 mt-1">Paste any link — Google Form, Drive file, GitHub repo, etc.</p>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Assignment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Grade Modal ─────────────────────────────────────────────────────────────

const GradeModal = ({ submission, onSave, onCancel, loading }) => {
  const [grade, setGrade] = useState(submission.grade || '');
  const [feedback, setFeedback] = useState(submission.feedback || '');
  const [formError, setFormError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!grade.trim()) return setFormError('Grade is required.');
    if (grade.trim().length > 10) return setFormError('Grade must be 10 characters or less.');
    onSave({ grade: grade.trim(), feedback: feedback.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="text-lg font-bold text-gray-900">Grade Submission</h3>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{formError}</div>
          )}

          {/* Student info */}
          <div className="p-4 bg-gray-50 rounded-xl space-y-2">
            <p className="text-sm font-medium text-gray-900">{submission.student_name || 'Student'}</p>
            <p className="text-sm text-gray-500">{submission.assignment_title}</p>
            <p className="text-xs text-gray-400">Submitted {formatDateTime(submission.submitted_at)}</p>
          </div>

          {/* Submission content */}
          {submission.submission_text && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Student's Answer</label>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-gray-800 whitespace-pre-wrap max-h-40 overflow-y-auto">
                {submission.submission_text}
              </div>
            </div>
          )}

          {submission.attachment_url && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Attachment</label>
              <a
                href={submission.attachment_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 text-secondary text-sm font-medium rounded-xl hover:bg-secondary/20 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View Attachment
              </a>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Grade (max 10 chars)</label>
            <input
              type="text"
              value={grade}
              onChange={(e) => { setGrade(e.target.value); setFormError(''); }}
              maxLength={10}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              placeholder="e.g. A+, 95, Pass"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Feedback (optional)</label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm resize-none"
              placeholder="Great work! Consider improving..."
            />
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Grade'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Submissions Panel ───────────────────────────────────────────────────────

const SubmissionsPanel = ({ assignment, onClose, onGraded }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grading, setGrading] = useState(null); // submission being graded
  const [gradeLoading, setGradeLoading] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const fetchSubs = async () => {
      try {
        const res = await api.get(`/api/assignments/${assignment.id}/submissions`);
        setSubmissions(res.data || []);
      } catch {
        setSubmissions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSubs();
  }, [assignment.id]);

  const handleGrade = async (data) => {
    setGradeLoading(true);
    try {
      await api.put(`/api/assignments/submissions/${grading.id}/grade`, data);
      setSubmissions((prev) =>
        prev.map((s) => s.id === grading.id ? { ...s, grade: data.grade, feedback: data.feedback, status: 'graded' } : s)
      );
      setGrading(null);
      setToast({ message: 'Grade saved successfully', type: 'success' });
      onGraded?.();
    } catch (err) {
      setToast({ message: getErrorMessage(err, 'Failed to save grade'), type: 'error' });
    } finally {
      setGradeLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-2xl animate-fade-in max-h-[90vh] overflow-y-auto">
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Submissions</h3>
            <p className="text-sm text-gray-500 mt-0.5">{assignment.title}</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-4 border border-gray-200 rounded-xl">
                  <div className="skeleton h-5 w-32 mb-2" />
                  <div className="skeleton h-4 w-48" />
                </div>
              ))}
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-sm font-medium text-gray-500">No submissions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {submissions.map((sub) => (
                <div key={sub.id} className="p-4 border border-gray-200 rounded-xl hover:bg-gray-50/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 mr-4">
                      <h4 className="font-medium text-gray-900">{sub.student_name || 'Student'}</h4>
                      <p className="text-xs text-gray-400 mt-0.5">Submitted {formatDateTime(sub.submitted_at)}</p>
                      {sub.submission_text && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{sub.submission_text}</p>
                      )}
                      {sub.attachment_url && (
                        <a href={sub.attachment_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-secondary mt-1 hover:underline">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Attachment
                        </a>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {sub.status === 'graded' ? (
                        <div className="text-center">
                          <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 text-sm font-bold rounded-full">{sub.grade}</span>
                          <button
                            onClick={() => setGrading(sub)}
                            className="block mt-1 text-xs text-gray-400 hover:text-gray-600"
                          >
                            Edit
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setGrading(sub)}
                          className="px-4 py-2 bg-[#ffb547] text-white text-sm font-medium rounded-xl hover:bg-[#ffb547]/90 transition-colors"
                        >
                          Grade
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {grading && (
        <GradeModal
          submission={{ ...grading, assignment_title: assignment.title }}
          onSave={handleGrade}
          onCancel={() => setGrading(null)}
          loading={gradeLoading}
        />
      )}
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────

const AssignmentManagement = () => {
  const [assignments, setAssignments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [viewingSubs, setViewingSubs] = useState(null); // assignment whose submissions we're viewing
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState('all'); // all | pending | graded

  const showToast = (message, type = 'success') => setToast({ message, type });

  const fetchAll = useCallback(async () => {
    try {
      const [assignmentsRes, batchesRes, classesRes] = await Promise.all([
        api.get('/api/assignments'),
        api.get('/api/batches'),
        api.get('/api/classes'),
      ]);
      setAssignments(assignmentsRes.data || []);
      setBatches(batchesRes.data || []);
      setClasses(classesRes.data || []);
    } catch (err) {
      setError('Failed to load assignments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleCreate = async (form) => {
    setCreateLoading(true);
    try {
      // form is already clean from CreateAssignmentModal.handleSubmit
      await api.post('/api/assignments', form);
      setShowCreate(false);
      showToast('Assignment created successfully');
      setLoading(true);
      await fetchAll();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to create assignment'), 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this assignment? This cannot be undone.')) return;
    try {
      await api.delete(`/api/assignments/${id}`);
      showToast('Assignment deleted');
      setAssignments((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to delete assignment'), 'error');
    }
  };

  const filteredAssignments = assignments.filter((a) => {
    if (filter === 'pending') return a.submission_count === 0 || new Date(a.due_date) > new Date();
    if (filter === 'graded') return a.submission_count > 0;
    return true;
  });

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="skeleton h-8 w-48" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="skeleton h-5 w-40 mb-2" />
                <div className="skeleton h-4 w-64 mb-2" />
                <div className="skeleton h-4 w-32" />
              </div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium">
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Assignments</h1>
              <p className="text-sm text-gray-500">{assignments.length} total</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Assignment
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'pending', label: 'Active' },
            { key: 'graded', label: 'Has Submissions' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                filter === f.key
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Assignment List */}
        {filteredAssignments.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center justify-center py-14 text-gray-400">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-500">No assignments found</p>
              <p className="text-xs text-gray-400 mt-1">Create your first assignment using the button above</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredAssignments.map((a) => {
              const urgency = dueUrgency(a.due_date);
              const urgencyStyles = {
                overdue: 'text-red-700 bg-red-50 border-red-200',
                urgent: 'text-red-600 bg-red-50 border-red-200',
                soon: 'text-amber-600 bg-amber-50 border-amber-200',
                normal: 'text-gray-600 bg-gray-50 border-gray-200',
              };
              const urgencyLabel = {
                overdue: 'Overdue',
                urgent: 'Due today',
                soon: 'Due soon',
                normal: `Due ${formatDate(a.due_date)}`,
              };

              return (
                <div key={a.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 mr-4">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                          {a.batch_name || 'Batch'}
                        </span>
                        {a.class_title && (
                          <span className="text-xs font-medium text-secondary bg-secondary/10 px-2.5 py-1 rounded-full">
                            {a.class_title}
                          </span>
                        )}
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${urgencyStyles[urgency]}`}>
                          {urgencyLabel[urgency]}
                        </span>
                      </div>
                      <h3 className="font-semibold text-gray-900 text-lg">{a.title}</h3>
                      {a.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{a.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                        <span>Created {formatDate(a.created_at)}</span>
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {a.submission_count || 0} submissions
                        </span>
                        {a.attachment_url && (
                          <a href={a.attachment_url} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            Attachment
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <button
                        onClick={() => setViewingSubs(a)}
                        className="px-4 py-2 bg-secondary text-white text-sm font-medium rounded-xl hover:bg-secondary/90 transition-colors"
                      >
                        View Submissions
                      </button>
                      <button
                        onClick={() => handleDelete(a.id)}
                        className="px-4 py-2 bg-red-50 text-red-600 text-sm font-medium rounded-xl hover:bg-red-100 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreate && (
        <CreateAssignmentModal
          batches={batches}
          classes={classes}
          onSave={handleCreate}
          onCancel={() => setShowCreate(false)}
          loading={createLoading}
        />
      )}

      {viewingSubs && (
        <SubmissionsPanel
          assignment={viewingSubs}
          onClose={() => setViewingSubs(null)}
          onGraded={() => {
            // Refresh assignments to update submission counts
            fetchAll();
          }}
        />
      )}
    </Layout>
  );
};

export default AssignmentManagement;
